// What a decided request was decided on (LO-199), as plain functions.
//
// On approve or reject the sandbox is torn down and its QA runs, call log and
// egress log go with it. The kernel freezes a record of what the decision relied
// on before it writes the decided request, and `GET /manifest-requests/{id}`
// carries it as `evidence`, read-only. Nothing here judges it: the report's
// verdict is the kernel's and is read with the same rules as a live report
// (`qaView`), and every sentence about what it does not prove is the record's.
import { qaView } from "./sandboxReview.js";

const DECIDED = new Set(["approved", "rejected"]);

/** A decided request that named a build: the only kind with a record to show. */
export function wantsEvidence(request) {
  return Boolean(request && DECIDED.has(request.state) && request.proposed?.build_id);
}

/** Where the record is read from: the request itself. */
export function evidenceRoute(requestId) {
  return `/manifest-requests/${encodeURIComponent(requestId)}`;
}

/**
 * The route that signs one screenshot the record kept:
 * `/manifest-requests/{id}/evidence/screenshots/{run}/{persona}/{file}`. A path
 * that is not `screenshots/<persona>/<file>` is refused here rather than sent,
 * because a browser resolves `..` before it asks.
 */
export function evidenceScreenshotRoute(requestId, runId, path) {
  const parts = String(path || "").split("/");
  if (parts.length !== 3 || parts[0] !== "screenshots" || parts.some((p) => !p || p === "." || p === "..")) {
    throw new Error("the record's path for it is not screenshots/<persona>/<file>");
  }
  return `/manifest-requests/${encodeURIComponent(requestId)}/evidence/screenshots/`
    + [runId, parts[1], parts[2]].map((p) => encodeURIComponent(String(p))).join("/");
}

/** Why a kept screenshot could not be had, in what the kernel's code means. */
export function evidenceFailure(error) {
  const code = String(error?.error || error?.body?.error || "");
  switch (code) {
    case "screenshot_not_kept":
      return `The record lists this screenshot and it was not kept: no copy of it is stored. ${error?.body?.detail || ""}`.trim();
    case "unknown_screenshot":
      return "The record of this decision does not list this screenshot, so the kernel will not serve it.";
    case "no_evidence":
      return "There is no record of what this request was decided on.";
    case "bad_screenshot_name":
      return "The record's name for this screenshot is not one the kernel accepts.";
    case "no_qa_runner":
      return "This kernel has no QA runner configured, so it cannot sign a screenshot.";
    default:
      break;
  }
  if (error?.status) return `The kernel answered ${error.status}${code ? ` ${code}` : ""}: ${error.message}`;
  return error?.message || "No reason was given.";
}

function callsOf(calls) {
  if (!calls?.available) {
    return { available: false, detail: String(calls?.detail || "The record holds no call log.") };
  }
  const s = calls.summary || {};
  const byPersona = Object.entries(s.by_persona || {}).map(([name, v]) => {
    const outcomes = v?.by_outcome || {};
    const refused = Object.entries(outcomes)
      .filter(([outcome]) => outcome !== "allowed" && outcome !== "empty")
      .reduce((n, [, count]) => n + Number(count || 0), 0);
    return { name, total: Number(v?.total || 0), refused, outcomes };
  });
  return {
    available: true,
    total: Number(s.total || 0),
    allowed: Number(s.allowed || 0),
    empty: Number(s.empty || 0),
    refused: Number(s.refused || 0),
    byPersona,
    unused: (calls.unused_grants || []).length,
    truncated: Boolean(calls.truncated),
    doesNotProve: calls.does_not_prove || [],
  };
}

function egressOf(egress) {
  if (!egress?.available) {
    return { available: false, detail: String(egress?.detail || "The record holds no egress log.") };
  }
  return {
    available: true,
    declared: egress.declared || [],
    hosts: (egress.hosts || []).map((h) => ({ host: String(h.host || ""), allowed: Boolean(h.allowed), count: Number(h.count || 0) })),
    gateway: egress.gateway || null,
    reading: egress.reading || null,
    doesNotProve: egress.does_not_prove || [],
  };
}

/**
 * The record, as what a screen shows. `kept: false` carries the kernel's reason;
 * anything else is the record read into the same shapes the live views use.
 */
export function evidenceView(evidence) {
  if (!evidence || evidence.kept !== true) {
    return {
      kept: false,
      detail: String(evidence?.detail || "The kernel did not say whether a record was kept."),
    };
  }
  const decision = String(evidence.decision || "");
  const relied = evidence.qa?.relied_on || null;
  const asked = evidence.qa?.as_requested || null;
  // A report kept for the decision is, by construction, for the manifest the
  // decision relied on: it is read as bound, never as "an earlier ceiling".
  const report = relied
    ? qaView({ run: { ...relied, bound_to_current: true }, gate: { passed_for_current: relied.status === "passed" } })
    : null;
  const files = evidence.screenshots?.files || [];
  const listedSet = new Set(files.map((f) => `${f.run_id}|${f.path}`));
  const landed = decision !== "approved" ? ""
    : evidence.landed === "after_rerun"
      ? `landed after its re-run passed${evidence.settled_by ? `, completed by ${evidence.settled_by}` : ""}`
      : "landed when it was made";
  return {
    kept: true,
    decision,
    heading: decision === "rejected" ? "What this was rejected on" : "What this was approved on",
    decidedBy: String(evidence.decided_by || ""),
    decidedAt: String(evidence.decided_at || ""),
    reason: String(evidence.reason || ""),
    landed,
    buildId: String(evidence.build_id || ""),
    manifestHash: String(evidence.manifest_hash || ""),
    shortHash: String(evidence.manifest_hash || "").slice(0, 12),
    frozenAt: String(evidence.frozen_at || ""),
    report,
    noRun: report ? "" : "There was no QA run bound to this decision's build and manifest when it was made.",
    asRequested: asked ? { runId: String(asked.run_id || ""), status: String(asked.status || ""), label: asked.label || null } : null,
    runs: evidence.qa?.runs || [],
    calls: callsOf(evidence.calls),
    egress: egressOf(evidence.egress),
    screenshots: files,
    listed: (runId, path) => listedSet.has(`${runId}|${path}`),
    retention: evidence.retention || {},
    doesNotProve: evidence.does_not_prove || [],
    trimmed: Boolean(evidence.trimmed),
  };
}
